import { useCallback, useEffect, useState } from 'react'
import { useClient } from '@/hooks/useClient'
import { useDebounce } from '@/hooks/useDebounce'

export type SavedFacility = {
  id: string
  name: string
  street: string
  city: string
  state: string
  zip: string
  fax: string
  phone: string
  /** Already part of the records-request facility directory. */
  tagged: boolean
}

/**
 * Facility search via the middleware `facilitySearch` oRPC procedure.
 *
 * - `scope: 'tagged'` (default) — the records-request directory (what the
 *   picker lists): facilities that were added/linked to this feature.
 * - `scope: 'all'` — every Organization on the platform (labs, radiology,
 *   care orgs, …) — the add-facility dedupe search.
 */
export function useRecordsFacilities(query: string, scope: 'tagged' | 'all' = 'tagged') {
  const client = useClient()
  const debouncedQuery = useDebounce(query, 300)
  const [facilities, setFacilities] = useState<SavedFacility[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const refetch = useCallback(() => setFetchKey((k) => k + 1), [])

  useEffect(() => {
    const q = debouncedQuery.trim()
    // The all-orgs search is dedupe-by-name; without a query there is nothing
    // to match (and the server returns nothing for it either).
    if (scope === 'all' && !q) {
      setFacilities([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    client
      .facilitySearch({ query: q || undefined, scope })
      .then(({ facilities: items }) => {
        if (cancelled) return
        setFacilities(
          items.map((item) => ({
            id: item.id,
            name: item.name,
            street: item.street ?? '',
            city: item.city ?? '',
            state: item.state ?? '',
            zip: item.zip ?? '',
            fax: item.fax ?? '',
            phone: item.phone ?? '',
            tagged: item.tagged,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) setFacilities([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, debouncedQuery, scope, fetchKey])

  return { facilities, loading, refetch }
}
